import { ContractSuretyType, type NaicsCodeId } from "@inrev/common";
import { useAdminUpdateBondRequest } from "@portal/api/bond-request/admin";
import type { AdminBondRequest } from "@portal/api/bond-request/admin/types";
import { cn } from "@portal/components/lib/utils";
import { createFormResolver } from "@portal/components/multiform";
import { Button, ClickableIcon } from "@portal/components/primitives";
import { useIsMinimalBondUIEnabled } from "@portal/hooks/useIsMinimalBondUIEnabled";
import { useVersionConflictSync } from "@portal/hooks/useVersionConflictSync";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
	getFormCompletion,
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	hasOwnPath,
	isEmpty,
} from "../../../utils/schema-fields";
import { AccountPartialSubmitConfirmationModal } from "../../shared/AccountPartialSubmitConfirmationModal";
import {
	VIEW_SECTION_STICKY_HEADER_CLASS,
	ViewSectionLayout,
} from "../../shared/layouts/ViewSectionLayout";
import {
	BondAmountCurrencyPercentInput,
	BondAmountInput,
	type BondRequestRowProps,
	ScopeOfWorkInput,
	SfaaCodeInput,
} from "../../shared/request/BondRequestDetailRows";
import { VIEW_SECTION_CARD_STACK_CLASS } from "../../shared/ViewSectionCardEditItem";
import { AdminBondingLineConflictModal } from "./AdminBondingLineConflictModal";
import {
	type ContractUpdateForm,
	getContractUpdateSchema,
	mapAdminBondRequestToContractUpdateForm,
} from "./mapAdminBondRequestToContractUpdateForm";

const EMPTY_NAICS_CODE_IDS: NaicsCodeId[] = [];

const BID_BOND_CURRENCY_PERCENT_NAMES = {
	referenceSourceName: "bidAmount",
	currencyName: "bondAmount",
	percentName: "bondAmountPercentOfBid",
	amountTypeName: "bidBondAmountType",
} as const;

type AdminBondRequestDetailsCardProps = {
	request: AdminBondRequest;
	accountNaicsCodes: NaicsCodeId[];
};

export const AdminBondRequestDetailsCard = ({
	request,
	accountNaicsCodes,
}: AdminBondRequestDetailsCardProps) => {
	const [editing, setEditing] = useState(false);
	const isMinimalUIEnabled = useIsMinimalBondUIEnabled();
	const requestRef = useRef(request);
	requestRef.current = request;
	const [partialSubmit, setPartialSubmit] = useState<{ percentComplete?: number } | null>(null);
	const [bondingLineConflictModalOpen, setBondingLineConflictModalOpen] = useState(false);
	const pendingSubmitDataRef = useRef<ContractUpdateForm | null>(null);
	const scopeOfWorkIds = useMemo(
		() => request.project?.scopeOfWork?.map((row) => row.id) ?? EMPTY_NAICS_CODE_IDS,
		[request.project?.scopeOfWork],
	);
	const schema = getContractUpdateSchema(request.contractSuretyType);
	const [initialValues] = useState(() => mapAdminBondRequestToContractUpdateForm(request));
	const formMethods = useForm<ContractUpdateForm>({
		defaultValues: initialValues,
		mode: "onBlur",
		reValidateMode: "onBlur",
		resolver: createFormResolver<ContractUpdateForm>(schema),
	});
	const { formState, handleSubmit } = formMethods;

	useVersionConflictSync(request.updatedAt, () => {
		if (!editing) formMethods.reset(mapAdminBondRequestToContractUpdateForm(request));
	});

	const { updateBondRequest, updateBondRequestIsLoading } = useAdminUpdateBondRequest(
		request.id,
		request.suretyAccountId,
		{
			onConflict: () => {
				formMethods.reset(mapAdminBondRequestToContractUpdateForm(requestRef.current));
				setEditing(false);
			},
			onBondingLineConflict: () => setBondingLineConflictModalOpen(true),
		},
	);
	const submitUpdate = (data: ContractUpdateForm) => {
		pendingSubmitDataRef.current = data;
		updateBondRequest(data, {
			onSuccess: () => {
				setEditing(false);
				pendingSubmitDataRef.current = null;
			},
		});
	};
	const onSubmit = (data: ContractUpdateForm) => {
		const emptyPaths = [
			...getPartialFieldPaths(schema, data),
			...getRequiredForApprovalFieldPaths(schema, data),
		].filter((path) => hasOwnPath(data, path) && isEmpty(getNestedValue(data, path)));

		if (emptyPaths.length > 0) {
			pendingSubmitDataRef.current = data;
			setPartialSubmit({ percentComplete: getFormCompletion(schema, data) });
			return;
		}
		submitUpdate(data);
	};
	const handleCancel = useCallback(() => {
		formMethods.reset(mapAdminBondRequestToContractUpdateForm(request));
		setEditing(false);
	}, [formMethods, request]);

	useEffect(() => {
		if (!editing || updateBondRequestIsLoading) return;
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") handleCancel();
		};
		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [editing, updateBondRequestIsLoading, handleCancel]);

	const componentProps: Omit<BondRequestRowProps<ContractUpdateForm>, "name"> = {
		formMethods,
		editing,
		schema,
		partialRequiresSubmit: false,
		isMinimalUIEnabled,
	};
	const editDisabled = request.status === "pending";

	return (
		<FormProvider {...formMethods}>
			<ViewSectionLayout
				header="Bond Details"
				partial={false}
				partialActive={false}
				headerClassName={editing ? VIEW_SECTION_STICKY_HEADER_CLASS : undefined}
				button={
					editing ? (
						<div className="flex space-x-[10px]">
							<Button
								variant="outline"
								color="secondary"
								size="sm"
								disabled={updateBondRequestIsLoading}
								onClick={handleCancel}
							>
								Cancel
							</Button>
							<Button
								variant="solid"
								color="primary"
								size="sm"
								disabled={!formState.isDirty}
								loading={updateBondRequestIsLoading}
								onClick={handleSubmit(onSubmit)}
							>
								Save
							</Button>
						</div>
					) : (
						<span
							title={editDisabled ? "Requests that are pending cannot be edited" : undefined}
							className={
								editDisabled
									? "inline-flex cursor-not-allowed [&_button]:pointer-events-none"
									: undefined
							}
						>
							<ClickableIcon
								type="pencil-square"
								size="mini"
								color="secondary"
								aria-label="Edit bond details"
								data-testid="admin-bond-request-details-edit"
								trackingId="bond-request-admin-details-edit"
								disabled={editDisabled}
								onClick={() => setEditing(true)}
							/>
						</span>
					)
				}
			>
				<div
					className={cn("w-full h-full bg-surface-base rounded-md", VIEW_SECTION_CARD_STACK_CLASS)}
				>
					{request.contractSuretyType === ContractSuretyType.bid ? (
						<BondAmountCurrencyPercentInput
							{...componentProps}
							bondAmount={request.bondAmount}
							requiredForApproval={request.bondAmount <= 0}
							currencyPercentNames={BID_BOND_CURRENCY_PERCENT_NAMES}
							name="bondAmount"
						/>
					) : (
						<BondAmountInput
							{...componentProps}
							bondAmount={request.bondAmount}
							requiredForApproval={request.bondAmount <= 0}
							name="bondAmount"
						/>
					)}
					<ScopeOfWorkInput
						{...componentProps}
						scopeOfWork={scopeOfWorkIds}
						accountNaicsCodes={accountNaicsCodes}
						name="project.scopeOfWork"
					/>
					<SfaaCodeInput
						{...componentProps}
						accountId={request.suretyAccountId}
						sfaaCode={request.sfaaCode}
						suretyType={request.suretyType}
						name="sfaaCodeId"
					/>
				</div>
				{partialSubmit && (
					<AccountPartialSubmitConfirmationModal
						context="view"
						userContext="admin"
						percentComplete={partialSubmit.percentComplete}
						onClose={() => setPartialSubmit(null)}
						onSubmit={() => {
							const pendingData = pendingSubmitDataRef.current;
							setPartialSubmit(null);
							if (pendingData) submitUpdate(pendingData);
						}}
					/>
				)}
				{bondingLineConflictModalOpen && (
					<AdminBondingLineConflictModal
						onDismiss={() => setBondingLineConflictModalOpen(false)}
						onMoveToReview={() => {
							setBondingLineConflictModalOpen(false);
							if (pendingSubmitDataRef.current) {
								updateBondRequest(pendingSubmitDataRef.current, {
									onSuccess: () => {
										setEditing(false);
										pendingSubmitDataRef.current = null;
									},
									confirm: false,
								});
							}
						}}
						onOverride={() => {
							setBondingLineConflictModalOpen(false);
							if (pendingSubmitDataRef.current) {
								updateBondRequest(pendingSubmitDataRef.current, {
									onSuccess: () => {
										setEditing(false);
										pendingSubmitDataRef.current = null;
									},
									confirm: true,
								});
							}
						}}
					/>
				)}
			</ViewSectionLayout>
		</FormProvider>
	);
};
